import React, { useState, useEffect } from 'react';
import {
  Upload,
  Download,
  FileSpreadsheet,
  Search,
  Edit2,
  Trash2,
  Loader,
  X,
  UserPlus,
  FileUp,
  Plus,
  Save,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  AlertCircle,
  Filter,
  BookOpen,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useData } from '../../contexts/DataContext';
import EXAM_API from '../../api/services/examApi';
import resultApi from '../../api/services/resultApi';
import sectionApi from '../../api/services/sectionApi';
import ManualResultEntry from './ManualResultEntry';
import ConfirmationModal from '../../ui/ConfirmationModal';

const ResultsManagement = () => {
  const { classes: apiClasses, loadStudents, currentAcademicYear } = useData();
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  
  // Section States
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [loadingSections, setLoadingSections] = useState(false);
  const [results, setResults] = useState([]);
  const [groupedResults, setGroupedResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [entryMode, setEntryMode] = useState('bulk');
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [editingResult, setEditingResult] = useState(null);
  const [editMarks, setEditMarks] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, resultId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (selectedExam && selectedClass) {
      loadResults();
    } else {
        setResults([]);
        setGroupedResults([]);
    }
  }, [selectedExam, selectedClass, selectedSection]);

  // Fetch sections when class changes
  useEffect(() => {
    if (!selectedClass) {
        setSections([]);
        setSelectedSection('');
        return;
    }
    
    const fetchSections = async () => {
        setLoadingSections(true);
        try {
            const response = await sectionApi.getSectionsByUnit(selectedClass);
            if (response.success) {
                setSections(response.data || []);
                // If there are sections, and none selected, maybe we stay on global
                // setSelectedSection(''); 
            }
        } catch (error) {
        } finally {
            setLoadingSections(false);
        }
    };
    fetchSections();
  }, [selectedClass]);

  // Load students for Manual Entry
  useEffect(() => {
    if (selectedClass) {
      loadStudents(selectedClass, selectedSection || null);
    }
  }, [selectedClass, selectedSection, loadStudents]);

  useEffect(() => {
    groupResultsByStudent();
  }, [results, searchTerm]);

  const loadExams = async () => {
    // Only load exams if a class is selected
    if (!selectedClass) {
      setExams([]);
      return;
    }

    try {
      const year = currentAcademicYear || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`;
      
      // Fetch only exams assigned to the selected class
      const response = await resultApi.getAssignedExamsForClass(selectedClass, year, selectedSection);
      
      if (response.success) {
        setExams(response.data || []);
      } else {
        setExams([]);
        toast.error('Failed to load assigned exams');
      }
    } catch (error) {
      toast.error('Failed to load exams for this class');
      setExams([]);
    }
  };

  useEffect(() => {
    loadExams();
  }, [selectedClass, selectedSection, currentAcademicYear]);

  const loadResults = async () => {
    setLoading(true);
    try {
      const response = await resultApi.getAllResults({
        examId: selectedExam,
        unitId: selectedClass,
        ...(selectedSection && { sectionId: selectedSection })
      });
      setResults(response.data?.results || response.data || []);
    } catch (error) {
      toast.error('Failed to load results');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const groupResultsByStudent = () => {
    let filtered = [...results];
    
    if (searchTerm) {
      filtered = filtered.filter(result =>
        result.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.studentRegistrationId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    const grouped = {};
    filtered.forEach(result => {
      const key = result.studentRegistrationId || result.studentId?._id;
      if (!grouped[key]) {
        grouped[key] = {
          studentId: key,
          studentName: result.studentName || `${result.studentId?.firstName} ${result.studentId?.lastName}`,
          registrationId: result.studentRegistrationId,
          subjects: []
        };
      }
      grouped[key].subjects.push(result);
    });

    const groupedArray = Object.values(grouped).map(student => {
      const totalMarks = student.subjects.reduce((sum, s) => sum + (s.totalMarks || 0), 0);
      const obtainedMarks = student.subjects.reduce((sum, s) => sum + (s.obtainedMarks || 0), 0);
      const percentage = totalMarks > 0 ? ((obtainedMarks / totalMarks) * 100).toFixed(2) : 0;
      const allPassed = student.subjects.every(s => s.isPassed || s.status === 'pass');
      
      return {
        ...student,
        totalMarks,
        obtainedMarks,
        percentage,
        status: allPassed ? 'pass' : 'fail',
        subjectCount: student.subjects.length
      };
    });

    setGroupedResults(groupedArray);
  };

  const toggleStudent = (studentId) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
    } else {
      newExpanded.add(studentId);
    }
    setExpandedStudents(newExpanded);
  };

  const handleDownloadTemplate = async () => {
    if (!selectedExam || !selectedClass) {
      toast.error('Please select exam and class first');
      return;
    }
    try {
      await resultApi.downloadTemplate(selectedExam, selectedClass, selectedSection || null);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setUploadFile(file);
    } else {
      toast.error('Please select an Excel file');
    }
  };

  const handleUploadResults = async () => {
    if (!uploadFile || !selectedExam) return;
    setUploading(true);
    try {
      const response = await resultApi.uploadBulkResults(uploadFile, selectedExam, selectedSection || null);
      toast.success(`✅ Uploaded ${response.data?.successCount || 0} results!`);
      setUploadFile(null);
      if (selectedClass) loadResults();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleEditResult = (result) => {
    setEditingResult(result._id);
    setEditMarks(result.obtainedMarks);
  };

  const handleSaveEdit = async (resultId) => {
    try {
      await resultApi.updateResult(resultId, { obtainedMarks: parseFloat(editMarks) });
      toast.success('Result updated!');
      setEditingResult(null);
      loadResults();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleCancelEdit = () => {
    setEditingResult(null);
    setEditMarks('');
  };

  const handleDeleteResult = (resultId) => {
    setDeleteModal({ open: true, resultId });
  };

  const confirmDeleteResult = async () => {
    if (!deleteModal.resultId) return;
    setIsDeleting(true);
    try {
      await resultApi.deleteResult(deleteModal.resultId);
      toast.success('Result deleted');
      setDeleteModal({ open: false, resultId: null });
      loadResults();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Manage Results</h2>
            <p className="text-xs text-slate-500">Enter results manually or upload them in bulk using an Excel template</p>
          </div>

          <div className="flex bg-gray-100 p-1 rounded-lg self-start">
            <button
              onClick={() => setEntryMode('bulk')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-2 ${
                entryMode === 'bulk' ? 'bg-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              style={entryMode === 'bulk' ? { color: 'var(--primary-color, #4f46e5)' } : {}}
            >
              <FileUp className="h-3.5 w-3.5" />
              Bulk Upload
            </button>
            <button
              onClick={() => setEntryMode('manual')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-2 ${
                entryMode === 'manual' ? 'bg-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              style={entryMode === 'manual' ? { color: 'var(--primary-color, #4f46e5)' } : {}}
            >
              <UserPlus className="h-3.5 w-3.5" />
              Manual Entry
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Step 1: Select Class */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-900 text-white text-xs font-bold">1</span>
              Select Class
            </label>
            <div className="relative group">
              <Filter className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedClass}
                onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setSelectedSection(''); 
                    setSelectedExam('');
                }}
                className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-10 pr-10 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all appearance-none cursor-pointer"
              >
                <option value="">Choose Class...</option>
                {apiClasses.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.unitName}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Step 2: Select Section (if class has sections) */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
              <span className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${selectedClass ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'}`}>2</span>
              Select Section {sections.length > 0 && <span className="text-red-500">*</span>}
            </label>
            <div className="relative group">
              <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedSection}
                onChange={(e) => {
                  setSelectedSection(e.target.value);
                  setSelectedExam('');
                }}
                disabled={!selectedClass}
                className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-10 pr-10 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50"
              >
                <option value="">{loadingSections ? 'Loading...' : sections.length === 0 ? 'No Sections' : 'Choose Section...'}</option>
                {sections.map(section => (
                  <option key={section._id} value={section._id}>
                    Section {section.sectionName || section.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {selectedClass && sections.length > 0 && !selectedSection && (
              <p className="text-xs text-red-600">Please select a section to continue</p>
            )}
          </div>

          {/* Step 3: Select Exam */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
              <span className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${(selectedClass && (sections.length === 0 || selectedSection)) ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'}`}>3</span>
              Select Exam
            </label>
            <div className="relative group">
              <BookOpen className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
                disabled={!selectedClass || (sections.length > 0 && !selectedSection)}
                className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-10 pr-10 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50"
              >
                <option value="">Choose Exam...</option>
                {exams.map(exam => (
                  <option key={exam._id || exam.id || exam.examId} value={exam._id || exam.id || exam.examId}>
                    {exam.examName} ({exam.examType})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {entryMode === 'bulk' && selectedExam && selectedClass && (
          <div className="mt-8 pt-8 border-t border-gray-100 flex flex-wrap items-center gap-4">
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-4 py-2 rounded-lg text-xs font-bold transition-all border"
              style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}
            >
              <Download className="h-3.5 w-3.5 mr-2" />
              Download Excel Template
            </button>
            <div className="flex-1" />
            <input
              type="file"
              id="bulk-upload"
              className="hidden"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
            />
            <label
              htmlFor="bulk-upload"
              className={`inline-flex items-center px-4 py-2 rounded-lg border-2 border-dashed text-xs font-bold cursor-pointer transition-all ${
                uploadFile ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 text-slate-500 hover:border-indigo-300 hover:bg-indigo-50'
              }`}
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
              {uploadFile ? uploadFile.name : 'Select Excel File'}
            </label>
            {uploadFile && (
                <button 
                  onClick={() => setUploadFile(null)}
                  className="p-1 hover:bg-red-50 text-red-500 rounded"
                >
                    <X size={16} />
                </button>
            )}
            <button
              onClick={handleUploadResults}
              disabled={!uploadFile || uploading}
              className="inline-flex items-center px-6 py-2 rounded-lg text-white text-xs font-bold disabled:opacity-50 shadow-sm transition-all btn-primary"
            >
              {uploading ? <Loader className="h-3.5 w-3.5 animate-spin mr-2" /> : <Upload className="h-3.5 w-3.5 mr-2" />}
              Upload Results
            </button>
          </div>
        )}

        {entryMode === 'manual' && selectedExam && selectedClass && (
          <div className="mt-8 pt-8 border-t border-gray-100">
            <ManualResultEntry
              selectedExam={selectedExam}
              selectedClass={selectedClass}
              selectedSection={selectedSection}
              onSuccess={loadResults}
            />
          </div>
        )}
      </div>

      {selectedExam && selectedClass && (
        <div className="space-y-6 pt-8 border-t border-gray-100">
          <div className="flex items-center justify-between p-2">
            <div className="flex items-center gap-4">
              <h3 className="text-sm font-bold text-slate-900">Student Results</h3>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-white border border-gray-200 rounded-lg py-1.5 pl-8 pr-3 text-xs w-64 outline-none input-field"
                />
              </div>
            </div>
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-gray-100 px-3 py-1 rounded-full">
              {groupedResults.length} Students found
            </div>
          </div>

          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 p-20 flex flex-col items-center justify-center">
              <Loader className="h-8 w-8 animate-spin text-indigo-600 mb-4" />
              <p className="text-sm font-medium text-slate-500">Loading student results...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {groupedResults.length > 0 ? (
                groupedResults.map((student) => (
                  <div key={student.studentId} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                      style={expandedStudents.has(student.studentId) ? { backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 5%, white)' } : {}}
                      onClick={() => toggleStudent(student.studentId)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg" style={expandedStudents.has(student.studentId) ? { backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)', color: 'var(--primary-color, #4f46e5)' } : { backgroundColor: '#f3f4f6', color: '#94a3b8' }}>
                          <ChevronDown className={`h-4 w-4 transition-transform ${expandedStudents.has(student.studentId) ? 'rotate-180' : ''}`} />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">{student.studentName}</h4>
                          <p className="text-[10px] text-slate-500 font-medium">ID: {student.registrationId}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-8">
                        <div className="text-right">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Aggregate</p>
                          <div className="flex items-baseline gap-1">
                            <span className={`text-lg font-bold ${student.status === 'pass' ? 'text-slate-900' : 'text-red-600'}`}>{student.percentage}%</span>
                          </div>
                        </div>
                        <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${student.status === 'pass' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {student.status === 'pass' ? 'Passed' : 'Failed'}
                        </div>
                      </div>
                    </div>

                    {expandedStudents.has(student.studentId) && (
                      <div className="p-4 bg-gray-50/50 border-t border-gray-100">
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-2 text-left font-bold text-slate-600 uppercase">Subject</th>
                                <th className="px-4 py-2 text-center font-bold text-slate-600 uppercase">Obtained / Total</th>
                                <th className="px-4 py-2 text-center font-bold text-slate-600 uppercase">Efficiency</th>
                                <th className="px-4 py-2 text-center font-bold text-slate-600 uppercase">Status</th>
                                <th className="px-4 py-2 text-right font-bold text-slate-600 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {student.subjects.map((subject) => (
                                <tr key={subject._id} className="hover:bg-gray-50/50 transition-colors">
                                  <td className="px-4 py-3">
                                    <p className="font-bold text-slate-900">{subject.subjectName}</p>
                                    {(() => {
                                      const subjectComponents = subject.markComponents || subject.markComponents || [];
                                      if (subjectComponents.length >= 2) {
                                        return (
                                          <div className="mt-1 space-y-0.5">
                                            {subjectComponents.map((comp, idx) => (
                                              <div key={idx} className="flex items-center gap-2 text-[10px] text-slate-500">
                                                <span className="font-medium">{comp.name}:</span>
                                                <span className="font-bold text-slate-700">{comp.obtainedMarks}/{comp.maxMarks}</span>
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {editingResult === subject._id ? (
                                      <div className="flex items-center justify-center gap-2">
                                        <input
                                          type="number"
                                          value={editMarks}
                                          onChange={(e) => setEditMarks(e.target.value)}
                                          className="w-16 bg-white rounded py-1 px-2 text-center font-bold outline-none input-field"
                                          style={{ color: 'var(--primary-color, #4f46e5)' }}
                                          autoFocus
                                        />
                                        <span className="text-slate-400">/ {subject.totalMarks}</span>
                                      </div>
                                    ) : (
                                      <span className="font-bold text-slate-900">{subject.obtainedMarks} <span className="text-slate-400 font-normal">/ {subject.totalMarks}</span></span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-col items-center gap-1.5">
                                      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full transition-all duration-500"
                                          style={{ width: `${subject.percentage}%`, backgroundColor: subject.percentage >= 75 ? 'var(--primary-color, #4f46e5)' : subject.percentage >= 40 ? '#334155' : '#ef4444' }}
                                        />
                                      </div>
                                      <span className="text-[10px] font-bold text-slate-500">{subject.percentage}%</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                                      subject.isPassed || subject.status === 'pass' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                    }`}>
                                      {subject.grade}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      {editingResult === subject._id ? (
                                        <>
                                          <button
                                            onClick={() => handleSaveEdit(subject._id)}
                                            className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-all"
                                          >
                                            <Save size={14} />
                                          </button>
                                          <button
                                            onClick={handleCancelEdit}
                                            className="p-1.5 bg-gray-200 text-slate-600 rounded hover:bg-gray-300 transition-all"
                                          >
                                            <X size={14} />
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => handleEditResult(subject)}
                                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                          >
                                            <Edit2 size={14} />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteResult(subject._id)}
                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-xl border border-dashed border-gray-300 p-20 flex flex-col items-center justify-center text-center">
                  <div className="p-4 bg-gray-50 rounded-full mb-4">
                    <FileSpreadsheet className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1">No results found</h3>
                  <p className="text-xs text-slate-500 max-w-xs">There are no records matching your selection. Use the manual entry or bulk upload to add data.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, resultId: null })}
        onConfirm={confirmDeleteResult}
        title="Delete Result"
        message="Are you sure you want to delete this subject result? This action cannot be undone."
        confirmText="Delete Result"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ResultsManagement;

